import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Typography } from '@startuptools/ui/Typography';
import { Alert } from '@startuptools/ui/Alert';
import { RadioGroup } from '@startuptools/ui/RadioGroup';
import { Radio } from '@startuptools/ui/Radio';
import { Trans } from '@lingui/react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { GqlShareIssue, GqlShareIssuePurpose } from '../../../../graphql/base-types.graphql';
import { isNil } from 'lodash-es';
import { DateTime } from 'luxon';
import { SipShareSubscriptionNoteListDocument } from '../../../common/components/documents/SipShareSubscriptionNoteList.graphql';
import { useSiUpdateMutation } from './ShareIssueStepper.graphql';
import { ShareIssueDocument } from '../../ShareIssue.graphql';
import { useStepper } from '../../../../components/steppers/common/StepperContext';

type FormValues = { purpose: 'qeso' | 'capital_preferential' | 'capital_non_preferential' };

export const SiStepPurpose = ({
  shareIssue,
}: {
  shareIssue: Pick<
    GqlShareIssue,
    'id' | 'companyId' | 'purpose' | 'preferentialRights' | 'subscriptionStartDate' | 'subscriptionEndDate'
  >;
}) => {
  const { stepNext } = useStepper();
  const [doSubmit, { loading }] = useSiUpdateMutation({
    refetchQueries: [ShareIssueDocument, SipShareSubscriptionNoteListDocument],
    awaitRefetchQueries: true,
  });

  const purpose =
    shareIssue.purpose === GqlShareIssuePurpose.Qeso
      ? 'qeso'
      : shareIssue.preferentialRights
        ? 'capital_preferential'
        : 'capital_non_preferential';

  const { control, handleSubmit } = useForm<FormValues>({ defaultValues: { purpose } });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    let subscriptionEndDate = !isNil(shareIssue.subscriptionEndDate)
      ? DateTime.fromISO(shareIssue.subscriptionEndDate)
      : null;
    if (!isNil(shareIssue.subscriptionStartDate) && !isNil(subscriptionEndDate)) {
      const subStartDate = DateTime.fromISO(shareIssue.subscriptionStartDate);
      if (purpose === 'capital_preferential' && subscriptionEndDate.diff(subStartDate, 'days').days < 14) {
        subscriptionEndDate = subStartDate.plus({ days: 14 });
      }
    }

    await doSubmit({
      variables: {
        companyId: shareIssue.companyId,
        input: {
          id: shareIssue.id,
          subscriptionEndDate: subscriptionEndDate?.toUTC(0, { keepLocalTime: true }).toISO(),
          purpose: values.purpose === 'qeso' ? GqlShareIssuePurpose.Qeso : GqlShareIssuePurpose.Capital,
          preferentialRights: values.purpose === 'capital_preferential',
        },
      },
    });

    await stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Purpose" />
        </Typography>
        <Alert>
          <Trans id='A rights issue means that the shares are primarily allocated to those who are already shareholders in the company. If you want the shares to go to a new investor, a so-called "directed issue," you choose without preferential rights. A rights issue must have a subscription period of at least two weeks.' />
        </Alert>

        <Controller
          control={control}
          name="purpose"
          render={({ field: { value, onChange, ...field } }) => (
            <RadioGroup {...field} value={value} onChange={e => onChange(e.target.value)}>
              <Radio value="capital_preferential" label='Med företräde ("företrädesemission")' />
              <Radio
                value="capital_non_preferential"
                label='Utan företräde med syfte att ta in kapital ("riktad emission")'
              />
              <Radio
                value="qeso"
                label="Utan företräde med syfte att ge ut aktier till innehavare av kvalificerade personaloptioner"
              />
            </RadioGroup>
          )}
        />

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
